import { Link } from "react-router-dom";

import { RenderWhen, RLCard } from "../../../../../../components";
import { SECTION_PATH } from "../../../../../../path";
import { COMPONENT_IDS } from "../../../../consts";
import { useLocalSampleSummaryParams } from "../../../../hooks";
import { isAdmin } from "../../../../../../utils";
import { DESCRIPTIONS } from "../../../layout-editor/consts";
import useConnectorsData from "../../../sources/hooks/useConnectorsData";
import useConnectorsEnabled from "../../../sources/hooks/useConnectorsEnabled";
import { Tooltip } from "../common";

import { formatCount } from "../../../../../../components/PageResultsCount";
import ConnectorsGrid from "./ConnectorsGrid";
import ConnectorsPromo from "./ConnectorsPromo";
import styles from "./connectors.module.scss";

// The summary card is a preview: show the 5 most recent rows (limited server-side), while the
// header count reflects the true total. A future dedicated Connectors screen fetches all rows.
const CARD_ROW_LIMIT = 5;

const ConnectorsCard = () => {
  const { hash } = useLocalSampleSummaryParams();

  // Synchronous - reads the permissions already on `window`, no request. Only decides whether the
  // promo links to the (admin-gated) configuration screen; the promo itself is shown to everyone.
  const isAdminUser = isAdmin();
  const anyConnectorEnabled = useConnectorsEnabled();

  const {
    data: connectorUploads,
    totalCount,
    isError,
    isSuccess,
    isLoading
  } = useConnectorsData(hash || "", CARD_ROW_LIMIT);

  // Two conditions, and both have to be a positive answer rather than an absent one:
  //   - nothing is configured (`=== false`, so a still-loading or failed request keeps the card -
  //     claiming there are no connectors because a subprocess failed is worse than an empty grid), and
  //   - this sample has no connector history (`isSuccess`, since `totalCount` defaults to 0 and would
  //     otherwise read as "empty" before the response lands).
  // A disabled connector's past events are still worth showing, so data wins over configuration.
  // The promo replaces the whole card, header included: there is no count or "See All" to show.
  if (anyConnectorEnabled === false && isSuccess && totalCount === 0) {
    return <ConnectorsPromo isAdmin={isAdminUser} />;
  }

  return (
    <RLCard
      header={
        <RLCard.LinkHeader
          title={
            <Tooltip
              placement="top"
              message={DESCRIPTIONS[COMPONENT_IDS.CONNECTORS]}
            >
              <span>{`Connectors (${formatCount(totalCount)})`}</span>
            </Tooltip>
          }
          LinkComponent={
            <RenderWhen condition={Boolean(totalCount)}>
              <Link
                className={styles.link}
                to={SECTION_PATH.SOURCES_CONNECTORS}
                data-testid="report_summary_connectors_see_all_link"
              >
                See All Connectors
              </Link>
            </RenderWhen>
          }
        />
      }
      content={
        <RLCard.Content data-testid="report_summary_connectors_grid">
          <ConnectorsGrid
            data={connectorUploads}
            isLoading={isLoading}
            isError={isError}
            hidePaging
          />
        </RLCard.Content>
      }
      data-testid="report_summary_connectors_card"
    />
  );
};

export default ConnectorsCard;
